import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { feeApi } from '../../../api/services';
import { 
  Users,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  FileText,
  Bell,
  Clock,
  BookOpen,
  Plus,
  Search
} from 'lucide-react';
import { FeeReminderForm } from '../../FeeReminders';
import PaymentModal from './PaymentModal'; // Import the multi-category PaymentModal

const RecordsByClassSection = ({
  classes,
  students,
  feeRecords,
  loading,
  expandedClasses,
  setExpandedClasses,
  filters,
  setFilters,
  formatCurrency,
  onAssignStructure,
  onExpandClass
}) => {
    // State for local expansion of fee records (not managed by parent)
    const [expandedRecords, setExpandedRecords] = useState(new Set()); 
    const [receiptLoading, setReceiptLoading] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPaymentData, setSelectedPaymentData] = useState(null); // { student, feeRecord, category }
    
    // Reminder Modal State
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [selectedReminderData, setSelectedReminderData] = useState(null);

    const handleOpenReminder = (student, feeRecord) => {
        setSelectedReminderData({
            studentObj: student,
            feeRecord: feeRecord,
            studentName: `${student.firstName} ${student.lastName}`,
            studentId: student._id,
            className: student.enrollment?.unitId?.unitName || 'N/A', // Assuming unitId is populated
            amount: feeRecord?.balance || 0,
            phoneNumber: student.studentPhone,
            parentPhone: student.parentPhone
        });
        setShowReminderModal(true);
    };

    const handleOpenPayment = (student, feeRecord, category) => {
      setSelectedPaymentData({
        student,
        feeRecord,
        selectedCategory: category, // Pre-select this category
        paymentMode: 'multi' // Use multi-category mode
      });
      setShowPaymentModal(true);
    };

  const handleOpenGeneralPayment = (student, feeRecord) => {
      if (!feeRecord || feeRecord.balance <= 0) return;
      
      setSelectedPaymentData({
        student,
        feeRecord,
        selectedCategory: null, 
        paymentMode: 'multi' // Multi-category or waterfall
      });
      setShowPaymentModal(true);
  };

  const handleDownloadReceipt = async (paymentId) => {
    if (!paymentId) {
      toast.error('Invalid payment ID');
      return;
    }
    
    setReceiptLoading(paymentId);
    try {
      const blob = await feeApi.getReceiptPdf(paymentId);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty receipt file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Receipt download error:', error);
      toast.error(error.message || 'Failed to download receipt. Please try again.');
    } finally {
      setReceiptLoading(null);
    }
  };

  const handleWhatsAppReceipt = async (paymentId) => {
    if (!paymentId) {
      toast.error('Invalid payment ID');
      return;
    }
    
    setReceiptLoading(paymentId);
    try {
      const res = await feeApi.sendReceiptWhatsapp(paymentId);
      if (res.success) {
        toast.success('Receipt sent via WhatsApp successfully');
      } else {
        toast.error(res.message || 'Failed to send receipt via WhatsApp');
      }
    } catch (error) {
      console.error('WhatsApp receipt error:', error);
      toast.error(error.message || 'Failed to send receipt via WhatsApp. Please try again.');
    } finally {
      setReceiptLoading(null);
    }
  }; 

  const toggleClassExpansion = (classId) => {
    setExpandedClasses(prev => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
      } else {
        next.add(classId);
      }
      return next;
    });
  };

  const toggleRecordExpansion = (recordId) => {
    setExpandedRecords(prev => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  };

  const handleClassExpand = (classId) => {
      toggleClassExpansion(classId);
      if (!expandedClasses.has(classId)) {
          // If opening, fetch students
          onExpandClass(classId);
      }
  };

  // Get students grouped by class (Data is already filtered by API)
  const studentsByClass = useMemo(() => {
    const grouped = {};
    
    // Group only valid students that are present in the filtered records
    // OR show all students if no specific record filtering is applied (depending on UX preference)
    // Here we will iterate classes and match students who are in the current `feeRecords` or `students` list.

    classes.forEach(cls => {
      const classId = cls._id;
      
      // If a class filter is active and it's not this class, skip (optimization)
      if (filters.classId !== 'all' && filters.classId !== classId) return;

      // Find students for this class from the passed `students` prop
      // Note: The `students` prop in `Fees.js` might need to be refreshed if we want it to strictly match search results
      // But `feeRecords` is definitely filtered.
      
      const classStudents = students.filter(s => {
        const studentClassId = s.enrollment?.unitId?._id || s.enrollment?.unitId || s.unitId?._id || s.unitId;
        return studentClassId === classId;
      });
      
      // Pair with fee records
      // Since `feeRecords` is filtered from backend, if we are filtering by status 'Overdue', 
      // we only get overdue records. Students without records won't show up if we strictly drive by records.
      // However, usually we want to show the student row even if filtered? 
      // If the User filters for "Overdue", they expect to see only students with Overdue records.
      
      const filteredStudentList = classStudents.map(student => {
          const studentRecords = feeRecords.filter(r => {
              const rSid = r.studentId?._id || r.studentId;
              return rSid === student._id;
          });
          return { student, feeRecords: studentRecords };
      }).filter(item => {
          // If filtering by status/search, we generally only want to show matches.
          // If `feeRecords` from API is empty, it means no match found (for status/search).
          // EXCEPT if searching for student name but they have no record? 
          // The API search param searches student name too. So `feeRecords` should return matches.
          
          // API `getAllRecords` returns Fee Records. It does NOT return students without records.
          // If we want to show filtered results, we should check if `studentRecords` has items.
          // UNLESS the filter is "All", then we show everyone.
          
          if (filters.status !== 'all' || filters.search) {
              return item.feeRecords.length > 0;
          }
          return true; 
      });

      if (filteredStudentList.length > 0) {
          grouped[classId] = {
            class: cls,
            students: filteredStudentList
          };
      }
    });

    return grouped;
  }, [classes, students, feeRecords, filters.classId, filters.status, filters.search]);

  // Filter classes based on selected filter (only useful if filters.classId is not 'all')
  const filteredClasses = useMemo(() => {
    const classArray = classes
      .map(cls => studentsByClass[cls._id])
      .filter(item => item !== undefined);

    if (filters.classId === 'all') return classArray;
    return classArray.filter(data => data.class._id === filters.classId);
  }, [classes, studentsByClass, filters.classId]);

  // Calculate class stats
  const getClassStats = (classStudents) => {
    const stats = {
      total: classStudents.length,
      paid: 0,
      pending: 0,
      overdue: 0,
      partial: 0,
      totalAmount: 0,
      totalPaid: 0,
      balance: 0
    };

    classStudents.forEach(({ feeRecords }) => {
      if (feeRecords && feeRecords.length > 0) {
        feeRecords.forEach(feeRecord => {
            stats.totalAmount += feeRecord.totalAmount || 0;
            stats.totalPaid += feeRecord.totalPaid || 0;
            stats.balance += feeRecord.balance || 0;
            
            const hasOverdue = feeRecord.breakdown?.some(cat => 
              cat.status === 'Due' && cat.dueDate && new Date(cat.dueDate) < new Date()
            );
            const hasPartial = feeRecord.totalPaid > 0 && feeRecord.balance > 0;
            
            if (feeRecord.balance === 0) {
              stats.paid++;
            } else if (hasOverdue) {
              stats.overdue++;
            } else if (hasPartial) {
              stats.partial++;
            } else {
              stats.pending++;
            }
        });
      } else {
        // Student with no fee record is counted as pending
        stats.pending++;
      }
    });

    return stats;
  };

  // Determine overall status for a record
  const getOverallStatus = (feeRecord) => {
    if (!feeRecord) return { status: 'No Record', color: 'bg-gray-100 text-gray-800', icon: Clock };
    
    if (feeRecord.balance === 0) return { status: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle };

    const hasOverdue = feeRecord.breakdown?.some(cat => 
      cat.status === 'Due' && cat.dueDate && new Date(cat.dueDate) < new Date()
    );
    
    if (hasOverdue) return { status: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    if (feeRecord.totalPaid > 0 && feeRecord.balance > 0) return { status: 'Partial', color: 'bg-yellow-100 text-yellow-800', icon: Clock };

    return { status: 'Due', color: 'bg-gray-100 text-gray-800', icon: Clock };
  }

  return (
    <div className="space-y-4">
      <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="p-1.5 rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)' }}>
              <BookOpen className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }}/>
            </div>
            <h2 className="ml-2 text-sm font-bold text-gray-900">Student Fee Records</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onAssignStructure} className="btn-primary text-xs flex items-center space-x-1.5">
              <Plus className="h-3.5 w-3.5" />
              <span>Assign Structure</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-3 flex flex-col md:flex-row gap-2">
           <select
            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-indigo-500 focus:border-indigo-500 w-full md:w-32"
            value={filters.classId}
            onChange={(e) => setFilters(prev => ({ ...prev, classId: e.target.value, search: '' }))}
          >
            <option value="all">All Classes</option>
            {classes.map(cls => (
              <option key={cls._id} value={cls._id}>{cls.unitName}</option>
            ))}
          </select>
          
          <select
            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-indigo-500 focus:border-indigo-500 w-full md:w-32"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Status</option>
            <option value="paid">Fully Paid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
            <option value="pending">Pending</option>
          </select>
          
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              className="px-2 py-1.5 border border-gray-300 rounded text-xs pl-8 w-full focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search by name or reg. ID"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>

          {(filters.classId !== 'all' || filters.status !== 'all' || filters.search) && (
            <button
              onClick={() => setFilters({ classId: 'all', status: 'all', search: '' })}
              className="px-2 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Class-First Grouping List */}
        {loading && filteredClasses.length === 0 ? (
          <div className="py-12 text-center text-gray-500">Loading fee records...</div>
        ) : filteredClasses.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No student fee records found matching the current filters.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredClasses.map(({ class: cls, students: classStudents }) => {
              // Determine if this class is expanded
              // The parent manages 'expandedClasses' via onExpandClass interaction usually, 
              // or we rely on the prop `expandedClasses` set which we are updating in handleClassExpand.
              const isExpanded = expandedClasses.has(cls._id);
              const stats = getClassStats(classStudents);

              return (
                <div key={cls._id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-shadow hover:shadow-md">
                   {/* Class Header - Always Visible */}
                  <div
                    onClick={() => handleClassExpand(cls._id)}
                    className="w-full flex items-center justify-between p-4 bg-white cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="p-2 rounded-lg" style={isExpanded ? { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)', color: 'var(--primary-color, #4f46e5)' } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}>
                        <BookOpen className="h-5 w-5" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            {cls.unitName}
                            <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {stats.total} Students
                            </span>
                        </h3>
                         {/* Class Summary Stats */}
                         <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                             <div className="flex items-center gap-1">
                                 <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                 <span>Paid: {stats.paid}</span>
                             </div>
                             <div className="flex items-center gap-1">
                                 <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                 <span>Overdue: {stats.overdue}</span>
                             </div>
                             <div className="flex items-center gap-1">
                                 <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                 <span>Partial: {stats.partial}</span>
                             </div>
                             <div className="flex items-center gap-1 font-medium text-gray-700 ml-2">
                                 <span>Total Due: {formatCurrency(stats.balance)}</span>
                             </div>
                         </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                         <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAssignStructure(cls._id);
                            }}
                            className="px-3 py-1.5 text-xs font-semibold rounded transition-colors"
                            style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'}
                         >
                            Assign Fee
                         </button>
                        {isExpanded ? (
                             <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                             <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                    </div>
                  </div>

                  {/* Class Detail Content - Lazy Loaded / Expanded */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-2 sm:p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                        {loading ? (
                             <div className="py-8 text-center text-gray-500 flex flex-col items-center">
                                 <div className="animate-spin h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full mb-2"></div>
                                 <span className="text-xs">Fetching records for {cls.unitName}...</span>
                             </div>
                        ) : classStudents.length === 0 ? (
                             <div className="py-8 text-center text-gray-500 text-sm bg-white rounded border border-gray-200 border-dashed">
                                 No students found in this class.
                             </div>
                        ) : (
                            // Render Table exclusively for this class
                            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-left">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Student</th>
                                                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Fee Structure</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Paid</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Balance</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {classStudents.map(({ student, feeRecords }) => {
                                                // Aggregate totals for the student
                                                const studentAggregates = feeRecords.reduce((acc, record) => {
                                                    acc.total += record.totalAmount || 0;
                                                    acc.paid += record.totalPaid || 0;
                                                    acc.balance += record.balance || 0;
                                                    return acc;
                                                }, { total: 0, paid: 0, balance: 0 });

                                                // Determine overall student status based on aggregated balance
                                                const hasAnyOverdue = feeRecords.some(r => {
                                                    return r.breakdown?.some(cat => cat.status === 'Due' && cat.dueDate && new Date(cat.dueDate) < new Date());
                                                });
                                                
                                                let studentStatus = 'Pending';
                                                let statusColor = 'bg-gray-100 text-gray-600';
                                                
                                                if (feeRecords.length === 0) {
                                                    studentStatus = 'No Record';
                                                } else if (studentAggregates.balance === 0) {
                                                    studentStatus = 'Paid';
                                                    statusColor = 'bg-green-100 text-green-700';
                                                } else if (hasAnyOverdue) {
                                                    studentStatus = 'Overdue';
                                                    statusColor = 'bg-red-100 text-red-700';
                                                } else if (studentAggregates.paid > 0) {
                                                    studentStatus = 'Partial';
                                                    statusColor = 'bg-yellow-100 text-yellow-700';
                                                } else {
                                                    studentStatus = 'Due';
                                                    statusColor = 'bg-orange-100 text-orange-700';
                                                }

                                                // Use student ID for expansion since we are grouping by student now
                                                const isStudentExpanded = expandedRecords.has(student._id);

                                                return (
                                                    <React.Fragment key={student._id}>
                                                        {/* Student Summary Row */}
                                                        <tr 
                                                            className="cursor-pointer transition-colors hover:bg-gray-50"
                                                            style={isStudentExpanded ? { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 3%, white)' } : {}}
                                                            onClick={() => toggleRecordExpansion(student._id)}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-semibold text-gray-900">{student.firstName} {student.lastName}</span>
                                                                    <span className="text-[10px] text-gray-500">{student.studentRegistrationId || 'No ID'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex flex-col">
                                                                    {feeRecords.length > 0 ? (
                                                                        <>
                                                                            <span className="text-xs text-gray-700 font-medium">
                                                                                {feeRecords.length} {feeRecords.length === 1 ? 'Structure' : 'Structures'}
                                                                            </span>
                                                                             <div className="flex gap-1 text-[9px] text-gray-400">
                                                                                {feeRecords.slice(0, 1).map((fr, i) => (
                                                                                    <span key={i} className="truncate max-w-[80px]">{fr.feeStructureId?.name}</span>
                                                                                ))}
                                                                                {feeRecords.length > 1 && <span>+{feeRecords.length - 1} more</span>}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-xs text-gray-400 italic">None</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                                                                {feeRecords.length > 0 ? formatCurrency(studentAggregates.total) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                                                                {feeRecords.length > 0 ? formatCurrency(studentAggregates.paid) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-sm font-bold text-red-600">
                                                                {feeRecords.length > 0 ? formatCurrency(studentAggregates.balance) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${statusColor}`}>
                                                                    {studentStatus}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <div className="flex items-center justify-end pr-2">
                                                                    {/* Removed cluttering 'Assign' buttons. Chevron implies expansion for details. */}
                                                                    {feeRecords.length > 0 ? (
                                                                        <div className="p-1 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'} onMouseOut={(e) => e.currentTarget.style.color = ''}>
                                                                             {isStudentExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-gray-300">-</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {/* Expanded Student Details: List of Fee Structures */}
                                                        {isStudentExpanded && feeRecords.length > 0 && (
                                                            <tr>
                                                                <td colSpan="7" className="px-0 py-0 border-b border-gray-100 bg-gray-50/50">
                                                                    <div className="px-4 py-3 space-y-3">
                                                                         {feeRecords.map((record) => {
                                                                              const { status: recStatus } = getOverallStatus(record);
                                                                              return (
                                                                                  <div key={record._id} className="bg-white border border-gray-200 rounded-md overflow-hidden shadow-sm">
                                                                                      {/* Record Header / Payment Actions */}
                                                                                      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-100">
                                                                                          <div className="flex items-center gap-3">
                                                                                              <div className="bg-white p-1.5 rounded border border-gray-200 shadow-sm">
                                                                                                  <FileText className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                                                                                              </div>
                                                                                              <div>
                                                                                                  <h4 className="text-sm font-bold text-gray-900">{record.feeStructureId?.name || 'Unknown Fee'}</h4>
                                                                                                  <p className="text-[10px] text-gray-500">{record.academicYear} • <span className={recStatus === 'Paid' ? 'text-green-600' : 'text-orange-600'}>{recStatus}</span></p>
                                                                                              </div>
                                                                                          </div>
                                                                                          <div className="flex items-center gap-3">
                                                                                               <div className="text-right mr-2 hidden sm:block">
                                                                                                   <div className="text-[10px] text-gray-500">Balance</div>
                                                                                                   <div className="text-sm font-bold text-red-600">{formatCurrency(record.balance)}</div>
                                                                                               </div>
                                                                                               {record.balance > 0 && (
                                                                                                    <>
                                                                                                        <button
                                                                                                            onClick={() => handleOpenReminder(student, record)}
                                                                                                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                                                                                            title="Send Reminder"
                                                                                                        >
                                                                                                            <Bell className="h-4 w-4" />
                                                                                                        </button>
                                                                                                        <button
                                                                                                            onClick={() => handleOpenGeneralPayment(student, record)}
                                                                                                            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 shadow-sm"
                                                                                                        >
                                                                                                            Pay Now
                                                                                                        </button>
                                                                                                    </>
                                                                                               )}
                                                                                          </div>
                                                                                      </div>

                                                                                      {/* Detailed Breakdown Grid */}
                                                                                      <div className="p-3 bg-white">
                                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                                                                                {record.breakdown?.map((cat, idx) => {
                                                                                                    const remaining = (cat.amount || 0) - (cat.paid || 0);
                                                                                                    return (
                                                                                                        <div key={idx} className="flex justify-between items-center p-2 rounded border border-gray-100 bg-gray-50/50">
                                                                                                            <div className="flex flex-col">
                                                                                                                <span className="text-xs font-semibold text-gray-700">{cat.categoryName}</span>
                                                                                                                <span className="text-[10px] text-gray-500">Paid: {formatCurrency(cat.paid)}</span>
                                                                                                            </div>
                                                                                                            <div className="text-right flex flex-col items-end">
                                                                                                                <span className="text-xs font-bold text-gray-900">{formatCurrency(cat.amount)}</span>
                                                                                                                {remaining > 0 ? (
                                                                                                                    <button 
                                                                                                                        onClick={() => handleOpenPayment(student, record, cat)}
                                                                                                                        className="text-[10px] hover:underline font-medium"
                                                                                                                        style={{ color: 'var(--primary-color, #4f46e5)' }}
                                                                                                                    >
                                                                                                                        Pay {formatCurrency(remaining)}
                                                                                                                    </button>
                                                                                                                ) : (
                                                                                                                    <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5"><CheckCircle className="h-3 w-3"/> Paid</span>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )
                                                                                                })}
                                                                                            </div>

                                                                                            {/* Transaction History Snippet */}
                                                                                            {(record.breakdown?.some(b => b.paymentDetails?.length > 0)) && (
                                                                                                <div className="mt-3 pt-2 border-t border-gray-100">
                                                                                                    <details className="group">
                                                                                                        <summary className="flex items-center gap-1 text-[10px] font-medium cursor-pointer select-none sticky top-0" style={{ color: 'var(--primary-color, #4f46e5)' }}>
                                                                                                            <Clock className="h-3 w-3" /> View Payment History
                                                                                                        </summary>
                                                                                                        <div className="mt-2 overflow-auto max-h-40">
                                                                                                            <table className="min-w-full text-[10px]">
                                                                                                                <thead className="bg-gray-50 text-gray-500">
                                                                                                                    <tr>
                                                                                                                        <th className="px-2 py-1 text-left">Date</th>
                                                                                                                        <th className="px-2 py-1 text-left">Txn ID</th>
                                                                                                                        <th className="px-2 py-1 text-right">Amount</th>
                                                                                                                        <th className="px-2 py-1 text-right">Receipt</th>
                                                                                                                    </tr>
                                                                                                                </thead>
                                                                                                                <tbody className="divide-y divide-gray-100">
                                                                                                                    {/* Flatten payments */}
                                                                                                                    {(() => {
                                                                                                                        const txns = [];
                                                                                                                        const seen = new Set();
                                                                                                                        record.breakdown?.forEach(c => c.paymentDetails?.forEach(p => {
                                                                                                                            if(!seen.has(p.transactionId)){ seen.add(p.transactionId); txns.push(p); }
                                                                                                                        }));
                                                                                                                        return txns.map((t, index) => (
                                                                                                                            <tr key={t.transactionId || index}>
                                                                                                                                <td className="px-2 py-1">{t.date ? new Date(t.date).toLocaleDateString() : 'N/A'}</td>
                                                                                                                                <td className="px-2 py-1 font-mono text-gray-400">
                                                                                                                                    {t.transactionId ? t.transactionId.toString().slice(-6) : 'N/A'}
                                                                                                                                </td>
                                                                                                                                <td className="px-2 py-1 text-right text-green-600 font-medium">{formatCurrency(t.amount || 0)}</td>
                                                                                                                                <td className="px-2 py-1 text-right">
                                                                                                                                    {t.transactionId && (
                                                                                                                                        <>
                                                                                                                                            <button 
                                                                                                                                                onClick={() => handleDownloadReceipt(t.transactionId)} 
                                                                                                                                                disabled={receiptLoading === t.transactionId}
                                                                                                                                                className="hover:underline mr-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                                                                                                                                                style={{ color: 'var(--primary-color, #4f46e5)' }} 
                                                                                                                                                onMouseOver={(e) => receiptLoading !== t.transactionId && (e.currentTarget.style.opacity = '0.8')} 
                                                                                                                                                onMouseOut={(e) => receiptLoading !== t.transactionId && (e.currentTarget.style.opacity = '1')}
                                                                                                                                            >
                                                                                                                                                {receiptLoading === t.transactionId ? '...' : 'PDF'}
                                                                                                                                            </button>
                                                                                                                                            <button 
                                                                                                                                                onClick={() => handleWhatsAppReceipt(t.transactionId)} 
                                                                                                                                                disabled={receiptLoading === t.transactionId}
                                                                                                                                                className="text-green-500 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                                                                            >
                                                                                                                                                {receiptLoading === t.transactionId ? '...' : 'WA'}
                                                                                                                                            </button>
                                                                                                                                        </>
                                                                                                                                    )}
                                                                                                                                </td>
                                                                                                                            </tr>
                                                                                                                        ))
                                                                                                                    })()}
                                                                                                                </tbody>
                                                                                                            </table>
                                                                                                        </div>
                                                                                                    </details>
                                                                                                </div>
                                                                                            )}
                                                                                      </div>
                                                                                  </div>
                                                                              )
                                                                         })}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>

                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedPaymentData && (
          <PaymentModal
            enrollmentId={selectedPaymentData.feeRecord?.studentEnrollmentId?._id || selectedPaymentData.student.enrollment?._id}
            category={selectedPaymentData.selectedCategory}
            paymentMode={selectedPaymentData.paymentMode}
            feeRecord={selectedPaymentData.feeRecord}
            feeApi={feeApi}
            formatCurrency={formatCurrency}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedPaymentData(null);
            }}
            onSuccess={() => {
              setShowPaymentModal(false);
              setSelectedPaymentData(null);
              window.location.reload(); // Refresh to show updated data
            }}
          />
        )}

        {/* Reminder Modal */}
        {showReminderModal && selectedReminderData && (
            <FeeReminderForm
                reminder={selectedReminderData}
                onClose={() => {
                    setShowReminderModal(false);
                    setSelectedReminderData(null);
                }}
                onRefresh={() => {
                    // Optional: Refresh records if needed, though reminder doesn't change fee data usually
                }}
                // Pass necessary props if FeeReminderForm needs them for context, 
                // though we are passing 'reminder' prop which should be enough for single student mode.
                students={[selectedReminderData.studentObj]} 
                classes={classes}
            />
        )}
      </div>
    </div>
  );
};

export default RecordsByClassSection;
